/**
 * src/components/ejecuciones/SelectorTenant.jsx
 * Elegir tenant sin recortarle el nombre.
 *
 * Antes eran píldoras: con seis tenants entraban, con treinta no. Y un `<select>`
 * nativo no vale aquí — no se puede pintar dos líneas dentro de una opción, y
 * hace falta la segunda para el convenio.
 *
 * Reglas que vienen de Manuel (26-08):
 *   · orden alfabético (lo hace el backend, con acentos bien resueltos)
 *   · nombre COMPLETO, sin abreviar ni recortar con puntos suspensivos
 *   · el convenio en el que opera, debajo
 */
import { useEffect, useRef, useState } from 'react'

/**
 * `cargando`/`fallo`/`total` (28-08): un desplegable que dice «0 activos» sin
 * haber leído afirma en falso — el §3 aplicado a una lista. Mientras se lee se
 * dice que se lee; si falló, que falló; y si el servidor SÍ respondió vacío,
 * `total` (tenants registrados sin filtrar) distingue «ninguno activo de N»
 * de «la tabla está vacía».
 */
export function SelectorTenant({ tenants = [], valor, onCambio,
                                 cargando = false, fallo = false, total = null }) {
  const [abierto, setAbierto] = useState(false)
  const caja = useRef(null)

  // Cerrar al pulsar fuera o con Escape: sin esto, el desplegable se queda
  // abierto tapando la línea de tiempo, que es lo que se vino a mirar.
  useEffect(() => {
    if (!abierto) return
    const fuera = e => { if (caja.current && !caja.current.contains(e.target)) setAbierto(false) }
    const escape = e => { if (e.key === 'Escape') setAbierto(false) }
    document.addEventListener('mousedown', fuera)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('mousedown', fuera)
      document.removeEventListener('keydown', escape)
    }
  }, [abierto])

  const elegido = tenants.find(t => t.cliente_id === valor)
  const etiqueta = elegido ? elegido.nombre : 'Todos los tenants'

  function elegir(id) {
    onCambio?.(id)
    setAbierto(false)
  }

  return (
    <div ref={caja} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setAbierto(a => !a)}
        aria-expanded={abierto}
        aria-haspopup="listbox"
        style={{
          display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
          fontFamily: 'var(--font-ui)', fontSize: 'var(--text-base)',
          padding: 'var(--space-2) var(--space-3)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-border-2)',
          background: 'var(--color-surface)', color: 'var(--color-text)',
          cursor: 'pointer', minWidth: '300px', textAlign: 'left',
        }}
      >
        <span style={{ flex: 1 }}>{etiqueta}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
             stroke="var(--color-text-3)" strokeWidth="2" aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {abierto && (
        <div
          role="listbox"
          style={{
            position: 'absolute', top: '42px', right: 0, width: '380px',
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border-2)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-md)',
            padding: 'var(--space-2)',
            zIndex: 'var(--z-dropdown)',
            maxHeight: '420px', overflowY: 'auto',
          }}
        >
          <div style={{
            padding: 'var(--space-2) var(--space-3) var(--space-1)',
            fontFamily: 'var(--font-mono)', fontSize: 'var(--text-micro)',
            letterSpacing: '0.12em', textTransform: 'uppercase',
            color: 'var(--color-text-3)',
          }}>
            Tenant
          </div>

          <Opcion
            activa={!valor}
            titulo="Todos los tenants"
            detalle={cargando ? 'leyendo…'
              : fallo ? 'sin lectura'
              : `${tenants.length} ${tenants.length === 1 ? 'activo' : 'activos'}`}
            onClick={() => elegir(null)}
            separada
          />

          {tenants.map(t => (
            <Opcion
              key={t.cliente_id}
              activa={t.cliente_id === valor}
              titulo={t.nombre}
              detalle={t.convenio_titulo
                ? `${t.convenio_titulo} · ${t.familia_cm}`
                : 'sin convenio asignado'}
              onClick={() => elegir(t.cliente_id)}
            />
          ))}

          {tenants.length === 0 && (
            <div style={{
              padding: 'var(--space-3)', fontFamily: 'var(--font-mono)',
              fontSize: 'var(--text-sm)',
              color: fallo ? 'var(--color-error)' : 'var(--color-text-3)',
              lineHeight: 1.5,
            }}>
              {cargando ? 'Leyendo los tenants…'
                : fallo ? '✗ No se pudo leer la lista de tenants'
                : total > 0
                  ? `El servidor no devolvió ningún tenant activo (hay ${total} registrados)`
                : total === 0 ? 'La tabla de clientes está vacía'
                : 'Sin tenants activos'}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Opcion({ activa, titulo, detalle, onClick, separada = false }) {
  return (
    <div
      role="option"
      aria-selected={activa}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } }}
      style={{
        display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start',
        padding: 'var(--space-2) var(--space-3)',
        borderRadius: 'var(--radius-sm)',
        background: activa ? 'var(--color-accent-soft)' : 'transparent',
        cursor: 'pointer',
        borderBottom: separada ? '1px solid var(--color-border)' : 'none',
        marginBottom: separada ? 'var(--space-1)' : 0,
      }}
      onMouseEnter={e => {
        if (!activa) e.currentTarget.style.background = 'var(--color-surface-2)'
      }}
      onMouseLeave={e => {
        if (!activa) e.currentTarget.style.background = 'transparent'
      }}
    >
      <span style={{ width: '14px', flexShrink: 0, marginTop: '3px' }}>
        {activa && (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
               stroke="var(--color-accent)" strokeWidth="2.4" aria-hidden="true">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </span>
      <div>
        {/* Sin `text-overflow`: el nombre completo se ve entero aunque haga
            dos líneas. Recortarlo era precisamente lo que Manuel no quería. */}
        <div style={{
          fontSize: 'var(--text-base)', color: 'var(--color-text)', lineHeight: 1.35,
        }}>
          {titulo}
        </div>
        <div style={{
          fontFamily: 'var(--font-mono)', fontSize: 'var(--text-micro)',
          color: 'var(--color-text-3)', marginTop: 'var(--space-05)',
        }}>
          {detalle}
        </div>
      </div>
    </div>
  )
}
